.field {
  display: contents;
}

.group {
  --ds-input-adornment-padding-inline-start: inherit;
  --ds-input-adornment-padding-inline-end: inherit;
  --ds-input-group-block-size: inherit;
  --ds-input-group-background: inherit;
  --ds-input-group-border-radius: inherit;
  --ds-input-group-border-block-width: inherit;
  --ds-input-group-border-inline-start-width: inherit;
  --ds-input-group-border-inline-end-width: inherit;

  --ds-input-group-block-size-default: 2rem;
  --ds-input-group-background-default: var(--color__white);
  --ds-input-padding-inline-start-default: 1rem;
  --ds-input-padding-inline-end-default: 0.5rem;
  --ds-input-padding-block-default: 0.5rem;
  --ds-input-adornment-margin-default: 0.25rem;
  --ds-input-adornment-padding-inline-start-default: var(--space__sm);
  --ds-input-adornment-padding-inline-end-default: var(--space__sm);
  --ds-input-group-border-radius-default: var(--border-radius__md);
  --ds-input-group-border-block-width-default: 1px;
  --ds-input-group-border-inline-start-width-default: 1px;
  --ds-input-group-border-inline-end-width-default: 1px;

  /*
   * The height, resolved once. Three places need it — the box, the input inside it, and the
   * adornment button that has to fit between them — and a height override that reached only two
   * of the three would leave the control a different size from its own contents.
   */
  --ds-input-group-block-size-resolved: var(--ds-input-group-block-size, var(--ds-input-group-block-size-default));

  --ds-input-adornment-size: calc(
    var(--ds-input-group-block-size-resolved) - 2px - 2 * var(--ds-input-adornment-margin-default)
  );

  flex: none;
  display: grid;
  grid-template-columns: minmax(auto, max-content) auto minmax(auto, max-content);
  /* Safari resolves the baseline of a flex-container adornment to its bottom edge,
     which drops the prefix/suffix to the input border. Center alignment matches
     the design intent (2rem-tall input, vertically centered content) and is robust
     across browsers. See PLT-4705. */
  align-items: center;
  min-block-size: var(--ds-input-group-block-size-resolved);

  border-inline-end-width: var(
    --ds-input-group-border-inline-end-width,
    var(--ds-input-group-border-inline-end-width-default)
  );
  border-inline-start-width: var(
    --ds-input-group-border-inline-start-width,
    var(--ds-input-group-border-inline-start-width-default)
  );
  border-block-width: var(--ds-input-group-border-block-width, var(--ds-input-group-border-block-width-default));
  border-radius: var(--ds-input-group-border-radius, var(--ds-input-group-border-radius-default));
  border-style: solid;
}

.group > input {
  block-size: calc(
    var(--ds-input-group-block-size-resolved) - 2 *
      var(--ds-input-group-border-block-width, var(--ds-input-group-border-block-width-default))
  );
}

/*
 * The group around a `TextArea`. Centring is right for a 32px single-line field and wrong for a
 * box several lines tall: the control has to fill the border box, or the reader's click lands on
 * padding and the caret sits away from the edge the text starts at. `vertical` resize only —
 * horizontal dragging would break the field out of the column it is laid out in.
 */
.groupMultiline {
  align-items: stretch;
}

/*
 * The doubled class outranks `.group > :is(input, textarea)` below, whose `line-height: none` is
 * right for a single-line input and would otherwise set wrapped prose solid, 14px on 14px.
 */
.groupMultiline.groupMultiline > textarea {
  resize: vertical;
  min-block-size: 5rem;
  line-height: var(--line-height__sm);
}

.group:not([data-disabled]) {
  background-color: var(--ds-input-group-background, var(--ds-input-group-background-default));
}

.group > :is(input, textarea) {
  grid-column: 2;
  border: none;
  background: transparent;
  /* so that the input can shrink */
  min-inline-size: 0;
  font-weight: var(--font-weight__regular);
  line-height: var(--line-height__none);
  font-family: var(--font-family__primary) sans-serif;
  font-size: var(--font-size__md);
  padding-block: var(--ds-input-padding-block-default);
  padding-inline-start: var(--input-padding-inline-start, var(--ds-input-padding-inline-start-default));
  padding-inline-end: var(--input-padding-inline-end, var(--ds-input-padding-inline-end-default));
}

/* The field's focus indicator is the ring on `.group` below, so the inner
   control must not paint one of its own. This has to out-specify the app-wide
   `input:focus-visible` baseline in reset.css — `.group > :is(input, textarea)`
   alone ties with it on specificity and would be decided by stylesheet order. */
.group > :is(input, textarea):focus-visible {
  outline: none;
}

/* `color`, not `border-color`: a `::placeholder` has no border to paint, so a colour declared as
   one is silently dropped and the user-agent grey wins. That grey is not one value — the same
   field measured #757575 in Chromium and #A8A8A8 in a reviewer's browser, i.e. 4.6:1 for one
   reader and 2.4:1 for another — which is why the fix is to declare the token rather than to
   pick a browser. `opacity: 1` because Firefox dims the placeholder by default, which would take
   the token back under the contrast floor. See DESIGN.md "A placeholder is `gray-500` on the
   field's white fill". */
.group > :is(input, textarea)::placeholder {
  color: var(--color__gray-500);
  opacity: 1;
}

.group:not([data-invalid], [data-hovered], [data-disabled]) {
  border-color: var(--color__gray-300);
}

.group[data-hovered]:not([data-invalid], [data-disabled]) {
  border-color: var(--color__gray-400);
}

.group[data-invalid] {
  border-color: var(--color__red-500);
}

/* A field has its own border, so the border *is* its focus line and no ring is
   added outside it (design/50-components.md "Focus and interaction states",
   PLT-6705). It used to get both: the border went `gray-400` and the platform
   ring was drawn around that, which said two things about one state. The inset
   shadow supplies the second pixel, so the line reaches the 2px the focus
   floor asks for without the box growing and shifting the form. */
/* prettier-ignore */
.group[data-focus-within]:not([data-invalid], :has(button[data-focus-visible])) {
  border-color: var(--focus-ring__color);
  box-shadow: inset 0 0 0 1px var(--focus-ring__color);
}

/* A field in error keeps its own colour through focus, so it is still one
   colour event: the same treatment, in red. */
.group[data-focus-within][data-invalid] {
  border-color: var(--color__red-500);
  box-shadow: inset 0 0 0 1px var(--color__red-500);
}

.group[data-disabled] {
  border-color: var(--color__gray-300);
  background-color: var(--color__gray-100);
}

.group:not([data-disabled]) :is(input, textarea) {
  color: var(--color__gray-800);
}

.group[data-disabled] :is(input, textarea) {
  color: var(--color__gray-400);
}

.adornment {
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--border-radius__sm);
  font-size: var(--font-size__md);

  --ds-button-block-size: var(--ds-input-adornment-size);
  --ds-button-border-start-radius: calc(
    var(--ds-input-group-border-radius, var(--ds-input-group-border-radius-default)) -
      var(--ds-input-adornment-margin-default) + 1px
  );
  --ds-button-border-end-radius: calc(
    var(--ds-input-group-border-radius, var(--ds-input-group-border-radius-default)) -
      var(--ds-input-adornment-margin-default) + 1px
  );
}

.adornment:first-child {
  grid-column: 1;
}

.adornment:last-child {
  grid-column: 3;
}

.adornment:not(:has(button)) {
  padding-inline-start: var(
    --ds-input-adornment-padding-inline-start,
    var(--ds-input-adornment-padding-inline-start-default)
  );
  padding-inline-end: var(
    --ds-input-adornment-padding-inline-end,
    var(--ds-input-adornment-padding-inline-end-default)
  );
}

.adornment:has(button) {
  padding-inline: var(--ds-input-adornment-margin-default);
}

.group:has(.adornment:first-child) {
  --input-padding-inline-start: 0;
}

.group:has(.adornment:last-child) {
  --input-padding-inline-end: 0;
}
